import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ChevronDown, ChevronRight } from "lucide-mui";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import type {
  PlatformSignalWorkflows,
  SignalWorkflowDay,
  SignalWorkflowLoad,
  TenantSignalWorkflows,
} from "../../../api/client.ts";
import { pathForTenant } from "../../../contexts/TenantContext.tsx";
import { useUrlSort, useUrlState } from "../../../hooks/useUrlState.ts";
import { formatNumber, percent, utcDayLabel } from "../../../utils/format.ts";
import ScopeToggle from "../../filters/ScopeToggle.tsx";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import Sparkline from "../../Sparkline.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import SortableTableHead, { type SortableColumn } from "../../table/SortableTableHead.tsx";
import { compactNumber, DAY_MS, drainLabel, duration, seconds } from "./queueFormat.ts";
import SampledAt from "./SampledAt.tsx";

const numeric = { fontVariantNumeric: "tabular-nums" } as const;
const muted = { color: "text.secondary" } as const;

type Scope = "active" | "all";

/** What a row shows, for one workflow or summed over a tenant's. */
interface Totals {
  name: string;
  pending: number | null;
  oldestPendingAt: string | null;
  drain: { text: string; growing: boolean } | null;
  evaluated1h: number | null;
  evaluated24h: number | null;
  matched24h: number | null;
  failed24h: number | null;
  p50Ms: number | null;
  tokens24h: number | null;
  modelMs24h: number | null;
  matched: number;
  qualified: number;
  trend: number[];
  days: SignalWorkflowDay[];
}

type SortKey = "name" | "pending" | "evaluated_24h" | "matched_24h" | "qualified" | "failed_24h" | "tokens_24h";

const SORT: Record<SortKey, (t: Totals) => number | string> = {
  name: (t) => t.name.toLowerCase(),
  pending: (t) => t.pending ?? 0,
  evaluated_24h: (t) => t.evaluated24h ?? 0,
  matched_24h: (t) => t.matched24h ?? 0,
  qualified: (t) => t.qualified,
  failed_24h: (t) => t.failed24h ?? 0,
  tokens_24h: (t) => t.tokens24h ?? 0,
};

const DEFAULT_SORT: SortKey = "tokens_24h";

/** The window's days oldest first, zeros where a day has no samples, so every row's bars line up. */
function padDays(daily: SignalWorkflowDay[], days: number, generatedAt: string): SignalWorkflowDay[] {
  const byDay = new Map(daily.map((d) => [Date.parse(d.day), d]));
  const today = Math.floor(Date.parse(generatedAt) / DAY_MS) * DAY_MS;
  return Array.from({ length: days }, (_, i) => {
    const t = today - (days - 1 - i) * DAY_MS;
    return (
      byDay.get(t) ?? {
        day: new Date(t).toISOString(),
        evaluated: 0,
        matched: 0,
        qualified: 0,
        failed: 0,
        pending: 0,
        tokens: 0,
      }
    );
  });
}

/** Null when no workflow has the figure yet (none sampled), else the sum. */
function sumOf(rows: SignalWorkflowLoad[], pick: (w: SignalWorkflowLoad) => number | null): number | null {
  const known = rows.map(pick).filter((n): n is number => n != null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
}

function workflowTotals(w: SignalWorkflowLoad, days: number, generatedAt: string): Totals {
  return {
    name: w.name,
    pending: w.pending,
    oldestPendingAt: w.oldest_pending_at,
    drain: w.pending == null ? null : drainLabel(w.pending, w.hours_to_drain),
    evaluated1h: w.evaluated_1h,
    evaluated24h: w.evaluated_24h,
    matched24h: w.matched_24h,
    failed24h: w.failed_24h,
    p50Ms: w.p50_ms,
    tokens24h: w.tokens_24h,
    modelMs24h: w.model_ms_24h,
    matched: w.matched,
    qualified: w.qualified,
    trend: w.trend.map((p) => p.pending ?? 0),
    days: padDays(w.daily, days, generatedAt),
  };
}

/**
 * A tenant's workflows summed. Its backlog clears when its slowest workflow's
 * does; p50 is its slowest workflow's (medians don't add). Backlog points
 * line up by sample time: one pass samples every workflow.
 */
function tenantTotals(t: TenantSignalWorkflows, rows: Totals[]): Totals {
  const ws = t.workflows;
  const waiting = sumOf(ws, (w) => w.pending);
  const backlogged = ws.filter((w) => w.pending);
  const growing = backlogged.some((w) => w.hours_to_drain == null);
  const hours = Math.max(0, ...backlogged.map((w) => w.hours_to_drain ?? 0));
  const oldest = ws
    .map((w) => w.oldest_pending_at)
    .filter((at): at is string => at != null)
    .sort()[0];
  const trend = new Map<string, number>();
  for (const w of ws) for (const p of w.trend) trend.set(p.at, (trend.get(p.at) ?? 0) + (p.pending ?? 0));
  const days = rows[0]?.days.map((d, i) => ({
    ...d,
    evaluated: rows.reduce((n, r) => n + r.days[i].evaluated, 0),
    matched: rows.reduce((n, r) => n + r.days[i].matched, 0),
    qualified: rows.reduce((n, r) => n + r.days[i].qualified, 0),
    failed: rows.reduce((n, r) => n + r.days[i].failed, 0),
    pending: rows.reduce((n, r) => n + r.days[i].pending, 0),
    tokens: rows.reduce((n, r) => n + r.days[i].tokens, 0),
  }));
  const p50s = ws.map((w) => w.p50_ms).filter((ms): ms is number => ms != null);
  return {
    name: t.tenant_name,
    pending: waiting,
    oldestPendingAt: oldest ?? null,
    drain: waiting == null ? null : drainLabel(waiting, growing ? null : hours),
    evaluated1h: sumOf(ws, (w) => w.evaluated_1h),
    evaluated24h: sumOf(ws, (w) => w.evaluated_24h),
    matched24h: sumOf(ws, (w) => w.matched_24h),
    failed24h: sumOf(ws, (w) => w.failed_24h),
    p50Ms: p50s.length ? Math.max(...p50s) : null,
    tokens24h: sumOf(ws, (w) => w.tokens_24h),
    modelMs24h: sumOf(ws, (w) => w.model_ms_24h),
    matched: ws.reduce((n, w) => n + w.matched, 0),
    qualified: ws.reduce((n, w) => n + w.qualified, 0),
    trend: [...trend.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, n]) => n),
    days: days ?? [],
  };
}

function sortRows<T extends { totals: Totals }>(rows: T[], key: SortKey, dir: "asc" | "desc"): T[] {
  const pick = SORT[key];
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = pick(a.totals);
    const y = pick(b.totals);
    const order = typeof x === "string" ? x.localeCompare(y as string) : x - (y as number);
    return sign * order || a.totals.name.localeCompare(b.totals.name);
  });
}

/**
 * Every tenant's signal workflows, one summed row per tenant that opens onto
 * its workflows: what each has waiting for the local model and when that
 * clears, what it evaluated, matched and research qualified, its failed calls,
 * latency and use of the rigs (tokens, model time), then its backlog over the
 * day and matches per day over the window. Sorts by any figure, heaviest
 * users of the rigs first. Counted from the five-minute queue samples.
 */
export default function SignalWorkflowsPanel({ data }: { data: PlatformSignalWorkflows }) {
  const [scope, setScope] = useState<Scope>("active");
  const url = useUrlState();
  const { sortBy, sortDir, handleSort } = useUrlSort(url, DEFAULT_SORT, "desc");
  const sortKey: SortKey = sortBy in SORT ? (sortBy as SortKey) : DEFAULT_SORT;
  // Names read A to Z first; every figure starts at its biggest.
  const onSort = (column: string) =>
    column === "name" && sortKey !== "name" ? url.changeFilter({ sort: "name", dir: "asc" }) : handleSort(column);
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(data.tenants.length === 1 ? [data.tenants[0].tenant_id] : []),
  );

  const paused = data.tenants.reduce((n, t) => n + t.workflows.filter((w) => !w.active).length, 0);
  const groups = useMemo(() => {
    const built = data.tenants
      .map((t) => ({ ...t, workflows: scope === "all" ? t.workflows : t.workflows.filter((w) => w.active) }))
      .filter((t) => t.workflows.length > 0)
      .map((t) => {
        const rows = t.workflows.map((w) => ({ workflow: w, totals: workflowTotals(w, data.days, data.generated_at) }));
        return { tenant: t, rows: sortRows(rows, sortKey, sortDir), totals: tenantTotals(t, rows.map((r) => r.totals)) };
      });
    return sortRows(built, sortKey, sortDir);
  }, [data, scope, sortKey, sortDir]);

  const all = groups.flatMap((g) => g.tenant.workflows);
  const waiting = sumOf(all, (w) => w.pending) ?? 0;
  const matched = all.reduce((n, w) => n + w.matched, 0);
  const qualified = all.reduce((n, w) => n + w.qualified, 0);
  const tokens24h = sumOf(all, (w) => w.tokens_24h) ?? 0;
  const allOpen = groups.length > 0 && groups.every((g) => open.has(g.tenant.tenant_id));

  const subtitle = [
    `${formatNumber(groups.length)} tenant${groups.length === 1 ? "" : "s"}`,
    waiting ? `${formatNumber(waiting)} waiting` : "Nothing waiting",
    `${compactNumber(tokens24h)} tokens 24h`,
    `${formatNumber(matched)} matched · ${formatNumber(qualified)} qualified in ${data.days} days`,
  ].join(" · ");

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const columns: SortableColumn[] = [
    { id: "name", label: "Tenant / workflow" },
    { id: "pending", label: "Waiting", align: "right" },
    { id: null, label: "Clears in", align: "right" },
    { id: "evaluated_24h", label: "Evaluated 24h", align: "right" },
    { id: "matched_24h", label: "Matched 24h", align: "right" },
    { id: "qualified", label: `Qualified ${data.days}d`, align: "right" },
    { id: "failed_24h", label: "Failed 24h", align: "right" },
    { id: "tokens_24h", label: "Tokens 24h", align: "right" },
    { id: null, label: "p50", align: "right" },
    { id: null, label: "Waiting, 24h" },
    { id: null, label: `Matched, ${data.days}d` },
  ];

  return (
    <Panel
      title="Signal workflows"
      subtitle={subtitle}
      count={all.length}
      disablePadding
      action={
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <SampledAt at={data.sampled_at} />
          {groups.length > 1 && (
            <Button
              size="small"
              onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map((g) => g.tenant.tenant_id)))}
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </Button>
          )}
          {paused > 0 && (
            <ScopeToggle<Scope>
              value={scope}
              onChange={setScope}
              aria-label="Which workflows"
              options={[
                { value: "active", label: "Active" },
                { value: "all", label: "All", badge: paused },
              ]}
            />
          )}
        </Stack>
      }
    >
      <ScrollTable>
        <SortableTableHead
          columns={columns}
          sortBy={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          leading={<TableCell padding="checkbox" />}
        />
        <TableBody>
          {groups.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} sx={muted}>
                No active signal workflows.
              </TableCell>
            </TableRow>
          )}
          {groups.map(({ tenant: t, rows, totals }) => {
            const expanded = open.has(t.tenant_id);
            return (
              <Fragment key={t.tenant_id}>
                <TableRow hover onClick={() => toggle(t.tenant_id)} sx={{ cursor: "pointer" }}>
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      aria-label={expanded ? `Hide ${t.tenant_name}'s workflows` : `Show ${t.tenant_name}'s workflows`}
                      aria-expanded={expanded}
                    >
                      {expanded ? <ChevronDown /> : <ChevronRight />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={pathForTenant(t.tenant_slug, "/workflows")}
                      onClick={(e) => e.stopPropagation()}
                      variant="body2"
                      underline="hover"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {t.tenant_name}
                    </Link>
                    <Typography variant="caption" component="div" sx={muted}>
                      {[
                        t.organization_name && t.organization_name !== t.tenant_name ? t.organization_name : null,
                        `${formatNumber(t.workflows.length)} workflow${t.workflows.length === 1 ? "" : "s"}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </TableCell>
                  <LoadCells totals={totals} allTokens={tokens24h} p50Caption={t.workflows.length > 1 ? "slowest" : null} />
                </TableRow>
                {expanded &&
                  rows.map(({ workflow: w, totals: wt }) => (
                    <WorkflowRow key={w.id} workflow={w} slug={t.tenant_slug} totals={wt} allTokens={tokens24h} />
                  ))}
              </Fragment>
            );
          })}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}

function DaysTooltip({ days }: { days: SignalWorkflowDay[] }) {
  return (
    <Box component="table" sx={{ borderSpacing: "8px 0", ...numeric }}>
      <tbody>
        <tr>
          <th />
          <th align="right">Evaluated</th>
          <th align="right">Matched</th>
          <th align="right">Qualified</th>
          <th align="right">Tokens</th>
        </tr>
        {[...days].reverse().map((d) => (
          <tr key={d.day}>
            <td>{utcDayLabel(Date.parse(d.day))}</td>
            <td align="right">{formatNumber(d.evaluated)}</td>
            <td align="right">{formatNumber(d.matched)}</td>
            <td align="right">{formatNumber(d.qualified)}</td>
            <td align="right">{compactNumber(d.tokens)}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function Caption({ children, sx }: { children: ReactNode; sx?: object }) {
  return (
    <Typography variant="caption" component="div" sx={{ ...muted, whiteSpace: "nowrap", ...sx }}>
      {children}
    </Typography>
  );
}

/** The figures after the name cell, the same for a tenant and a workflow. */
function LoadCells({
  totals: t,
  allTokens,
  p50Caption = null,
}: {
  totals: Totals;
  allTokens: number;
  p50Caption?: string | null;
}) {
  const pending = t.pending ?? 0;
  const stale = t.oldestPendingAt && Date.now() - Date.parse(t.oldestPendingAt) > DAY_MS;
  return (
    <>
      <TableCell align="right" sx={numeric}>
        <Tooltip title="Records of its type queued since it became active; its source filters may still drop some">
          <Typography variant="body2" sx={{ ...numeric, color: pending ? "text.primary" : "text.secondary" }}>
            {t.pending == null ? "–" : formatNumber(pending)}
          </Typography>
        </Tooltip>
        {t.oldestPendingAt && (
          <Caption sx={stale ? { color: "warning.main" } : undefined}>
            oldest <RelativeTime value={t.oldestPendingAt} />
          </Caption>
        )}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap", color: t.drain?.growing ? "warning.main" : "text.secondary" }}>
        {t.drain ? t.drain.text : "–"}
      </TableCell>
      <TableCell align="right" sx={numeric}>
        {t.evaluated24h == null ? "–" : formatNumber(t.evaluated24h)}
        {!!t.evaluated1h && <Caption>{formatNumber(t.evaluated1h)} last hour</Caption>}
      </TableCell>
      <TableCell align="right" sx={numeric}>
        {t.matched24h == null ? "–" : formatNumber(t.matched24h)}
        {!!t.evaluated24h && <Caption>{percent(t.matched24h ?? 0, t.evaluated24h)} of evaluated</Caption>}
      </TableCell>
      <TableCell align="right" sx={numeric}>
        <Typography variant="body2" sx={{ ...numeric, color: t.qualified ? "success.main" : "text.secondary" }}>
          {formatNumber(t.qualified)}
        </Typography>
        {t.matched > 0 && <Caption>of {formatNumber(t.matched)} matched</Caption>}
      </TableCell>
      <TableCell align="right" sx={{ ...numeric, color: t.failed24h ? "error.main" : "text.secondary" }}>
        {t.failed24h == null ? "–" : formatNumber(t.failed24h)}
      </TableCell>
      <TableCell align="right" sx={numeric}>
        <Tooltip title={t.tokens24h == null ? "" : `${formatNumber(t.tokens24h)} tokens in + out`}>
          <span>{t.tokens24h == null ? "–" : compactNumber(t.tokens24h)}</span>
        </Tooltip>
        {!!t.tokens24h && (
          <Caption>
            {percent(t.tokens24h, allTokens)} · {duration(t.modelMs24h)} model
          </Caption>
        )}
      </TableCell>
      <TableCell align="right" sx={{ ...numeric, whiteSpace: "nowrap", color: "text.secondary" }}>
        {seconds(t.p50Ms)}
        {p50Caption && t.p50Ms != null && <Caption>{p50Caption}</Caption>}
      </TableCell>
      <TableCell>
        {t.trend.length ? (
          <Sparkline
            variant="area"
            showRange={false}
            width={96}
            height={24}
            color={t.drain?.growing ? "var(--mui-palette-warning-main)" : undefined}
            values={t.trend}
          />
        ) : (
          "–"
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={<DaysTooltip days={t.days} />}>
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Sparkline
              showRange={false}
              width={112}
              height={24}
              color="var(--mui-palette-success-main)"
              values={t.days.map((d) => d.matched)}
            />
          </Box>
        </Tooltip>
      </TableCell>
    </>
  );
}

function WorkflowRow({
  workflow: w,
  slug,
  totals,
  allTokens,
}: {
  workflow: SignalWorkflowLoad;
  slug: string;
  totals: Totals;
  allTokens: number;
}) {
  const caption = [w.label, w.match_mode === "rules" ? "Rules" : "LLM", w.active ? null : "Paused"]
    .filter(Boolean)
    .join(" · ");

  return (
    <TableRow hover sx={{ bgcolor: "action.hover", ...(w.active ? {} : { opacity: 0.6 }) }}>
      <TableCell padding="checkbox" />
      <TableCell sx={{ pl: 3 }}>
        <Link
          component={RouterLink}
          to={pathForTenant(slug, `/workflows/${w.id}`)}
          variant="body2"
          underline="hover"
          sx={{ fontWeight: 500, color: "text.primary" }}
        >
          {w.name}
        </Link>
        <Typography variant="caption" component="div" sx={muted}>
          {caption}
          {w.active_since && (
            <>
              {" · active since "}
              <RelativeTime value={w.active_since} />
            </>
          )}
        </Typography>
      </TableCell>
      <LoadCells totals={totals} allTokens={allTokens} />
    </TableRow>
  );
}
